import type { CheckedCharacter } from "./xiezi-check.js";
import { markedLine, setbackLine } from "./xiezi-copy.js";
import type { NoteVerdict } from "./xiezi-view.js";
import {
  type DrillProgress,
  passDone,
  passMissed,
} from "../handwriting/xiezi-stages.js";

/**
 * Where a character written from memory leaves the reader once it is marked,
 * and what the line under the square says about it. `xiezi-drill.ts` moves
 * the reader there.
 */

/** A pass in the free stage, as marked. */
export interface MarkedPass {
  /** Where the reader is now, or nothing where the ink was not marked. */
  readonly after: DrillProgress | undefined;

  /** Whether the pass was passed. */
  readonly passed: boolean;

  /** The line under the square. */
  readonly line: string;

  /** Whether the line marks a pass or a miss, or neither where nothing was marked. */
  readonly verdict: NoteVerdict | undefined;
}

/**
 * A character written from memory, marked.
 *
 * A pass passed counts. A pass missed takes one back, and the line says how
 * far. A blank square and a character the index lacks move nothing.
 *
 * @param before - Where the reader was when they pressed Check.
 * @param checked - What the marker made of the ink.
 * @returns Where they are now, and the line.
 */
export function markedPass(
  before: DrillProgress,
  checked: CheckedCharacter,
): MarkedPass {
  const { marked, passed, reading } = checked;
  const after = !marked
    ? undefined
    : passed
      ? passDone(before)
      : passMissed(before);
  const setback =
    after === undefined || passed ? "" : ` ${setbackLine(before, after)}`;

  return {
    after,
    passed,
    line: checked.line ?? `${markedLine(reading, passed)}${setback}`,
    verdict: after === undefined ? undefined : passed ? "right" : "wrong",
  };
}
